import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'error'

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: t("contact.info.address_label"),
      value: t("contact.info.address"),
    },
    {
      icon: Phone,
      label: t("contact.info.phone_label"),
      value: t("contact.info.phone"),
    },
    {
      icon: Mail,
      label: t("contact.info.email_label"),
      value: t("contact.info.email"),
    },
    {
      icon: Clock,
      label: t("contact.info.hours_label"),
      value: t("contact.info.hours"),
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Page header */}
      <section className="py-20 bg-[#F4F1EA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center space-y-4">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4A6753]">
            {t("contact.overline")}
          </span>
          <h1
            data-testid="contact-page-title"
            className="font-heading text-4xl sm:text-5xl tracking-tight text-[#1E2922] font-semibold"
          >
            {t("contact.title")}
          </h1>
          <p className="text-base leading-relaxed text-[#566D60] max-w-2xl mx-auto">
            {t("contact.subtitle")}
          </p>
        </div>
      </section>

      {/* Contact content */}
      <section className="py-24 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
            {/* Contact form */}
            <div className="md:col-span-7">
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium text-[#1E2922]">
                      {t("contact.form.name")} *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="bg-white border-[#E2E0D9] rounded-xl focus:ring-2 focus:ring-[#4A6753]/20 focus:border-[#4A6753] text-[#1E2922]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-[#1E2922]">
                      {t("contact.form.email")} *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-email-input"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="bg-white border-[#E2E0D9] rounded-xl focus:ring-2 focus:ring-[#4A6753]/20 focus:border-[#4A6753] text-[#1E2922]"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-[#1E2922]">
                      {t("contact.form.phone")}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={handleChange}
                      className="bg-white border-[#E2E0D9] rounded-xl focus:ring-2 focus:ring-[#4A6753]/20 focus:border-[#4A6753] text-[#1E2922]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-sm font-medium text-[#1E2922]">
                      {t("contact.form.subject")}
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      data-testid="contact-subject-input"
                      value={form.subject}
                      onChange={handleChange}
                      className="bg-white border-[#E2E0D9] rounded-xl focus:ring-2 focus:ring-[#4A6753]/20 focus:border-[#4A6753] text-[#1E2922]"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm font-medium text-[#1E2922]">
                    {t("contact.form.message")} *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={5}
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={handleChange}
                    required
                    className="bg-white border-[#E2E0D9] rounded-xl focus:ring-2 focus:ring-[#4A6753]/20 focus:border-[#4A6753] text-[#1E2922] resize-none"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  disabled={status === "sending"}
                  className="bg-[#4A6753] text-white rounded-full px-8 py-3.5 font-medium hover:bg-[#3A5242] transition-colors shadow-sm disabled:opacity-60"
                >
                  {status === "sending"
                    ? t("contact.form.sending")
                    : t("contact.form.submit")}
                </button>
                {status === "success" && (
                  <p data-testid="contact-success-message" className="text-sm text-[#4A6753] font-medium">
                    {t("contact.form.success")}
                  </p>
                )}
                {status === "error" && (
                  <p data-testid="contact-error-message" className="text-sm text-red-600 font-medium">
                    {t("contact.form.error")}
                  </p>
                )}
              </form>
            </div>

            {/* Contact info sidebar */}
            <div className="md:col-span-5">
              <div className="bg-[#F4F1EA] rounded-2xl p-8 space-y-8">
                {contactInfo.map((info, i) => (
                  <div key={i} data-testid={`contact-info-${i}`} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#4A6753]/10 flex items-center justify-center shrink-0">
                      <info.icon size={18} className="text-[#4A6753]" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#1E2922]">
                        {info.label}
                      </h4>
                      <p className="text-sm text-[#566D60] whitespace-pre-line mt-1">
                        {info.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
